import {
  buildMultilineAction,
  getIndentation,
  getIndentationAndContent,
  getPrecedingLine,
  SelectedLinesInfo,
} from '../utils'
import { bulletRe, orderedRe } from '../listUtils'

type OrderedCtx = {
  allOrdered: boolean
  incrementIndex: boolean
  precedingLevel?: Level
}

type Level = { indent: string; idx: number; delim: string }
const DefaultLevel = { indent: '', idx: 1, delim: '.' }

const computeContext = (
  info: SelectedLinesInfo,
  { incrementIndex }: { incrementIndex: boolean } = { incrementIndex: false },
): OrderedCtx => {
  const { line: preLine } = getPrecedingLine(info.before, info.before.length)
  const preIndent = getIndentation(preLine)

  const preOrderedListItemMatch = preLine.match(orderedRe)
  const preIdx = preOrderedListItemMatch
    ? parseInt(preOrderedListItemMatch[2], 10)
    : undefined
  const preDelim = preOrderedListItemMatch
    ? preOrderedListItemMatch[3]
    : undefined

  return {
    allOrdered: info.lines.every((ln) => orderedRe.test(ln)),
    incrementIndex,
    precedingLevel: preIdx
      ? {
          indent: preIndent,
          idx: incrementIndex ? preIdx + 1 : preIdx,
          delim: preDelim || '.',
        }
      : undefined,
  }
}

const buildOrderedTransformer = ({
  allOrdered,
  incrementIndex,
  precedingLevel,
}: OrderedCtx) => {
  const stack: Level[] = precedingLevel ? [precedingLevel] : []
  return (ln: string) => {
    if (allOrdered) {
      const m = ln.match(orderedRe)!
      const indentation = m[1] || ''
      const content = ln.slice(m[0].length)
      return indentation + content
    }

    let indent: string, content: string

    const mb = ln.match(bulletRe)
    const mo = ln.match(orderedRe)
    const moDelim = mo ? mo[3] || '.' : undefined
    if (mb) {
      indent = mb[1] || ''
      content = ln.slice(mb[0].length)
    } else if (mo) {
      indent = mo[1] || ''
      content = ln.slice(mo[0].length)
    } else {
      ;[indent, content] = getIndentationAndContent(ln)
    }

    if (stack.length === 0) {
      stack.push({
        ...DefaultLevel,
        indent,
        delim: moDelim ?? DefaultLevel.delim,
      })
    } else {
      const top = stack[stack.length - 1]
      if (indent.length > top.indent.length) {
        stack.push({
          ...DefaultLevel,
          indent,
          delim: moDelim ?? DefaultLevel.delim,
        })
      } else {
        while (
          stack.length &&
          stack[stack.length - 1].indent.length > indent.length
        ) {
          stack.pop()
        }

        if (
          !stack.length ||
          stack[stack.length - 1].indent.length < indent.length
        ) {
          stack.push({
            ...DefaultLevel,
            indent,
            delim: moDelim ?? DefaultLevel.delim,
          })
        }
      }
    }

    const currentIndex = stack[stack.length - 1]
    const updatedLn = `${indent}${String(currentIndex.idx)}${currentIndex.delim} ${content}`

    if (incrementIndex) ++currentIndex.idx

    return updatedLn
  }
}

export const toggleOrderedListAction = buildMultilineAction(
  computeContext,
  buildOrderedTransformer,
)
